import React, { useMemo, useState } from 'react';
import Modal from 'react-modal';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList
} from 'recharts';
import './css/ResultsModal.css';

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const hours = [...Array(24).keys()];
const EMPTY_AGGREGATED_RESULTS = [];

const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

const parseSlot = (timeSlot) => {
  const [dayIndexRaw, hourRaw] = String(timeSlot).split('-');
  const dayIndex = Number(dayIndexRaw);
  const hour = Number(hourRaw);
  return { dayIndex, hour };
};

const formatSlotLabel = (timeSlot) => {
  const { dayIndex, hour } = parseSlot(timeSlot);
  const day = daysOfWeek[dayIndex] || `Day ${dayIndex}`;
  return `${day} ${formatHour(hour)}`;
};

const toSlotKey = (dayIndex, hour) => `${dayIndex}-${hour}`;

const getHeatColor = (count, maxCount, isAllowed) => {
  if (!isAllowed) return '#f1f5f9';
  if (!maxCount || count <= 0) return '#e6f0fb';
  const ratio = Math.max(0, Math.min(1, count / maxCount));
  const lightness = 93 - ratio * 48;
  return `hsl(210, 82%, ${lightness}%)`;
};

const ResultsModal = ({ isOpen, onRequestClose, vote }) => {
  const voteDoc = vote?.vote || {};
  const aggregatedResults = vote?.aggregatedResults || EMPTY_AGGREGATED_RESULTS;
  const { name = '', votes = [], type = '', restrictedTimeSlots = [] } = voteDoc;

  const [activeView, setActiveView] = useState('heatmap');
  const [selectedDays, setSelectedDays] = useState(daysOfWeek.map((_, index) => index));
  const [showUnavailableSlots, setShowUnavailableSlots] = useState(true);

  const allowedSlotSet = useMemo(() => {
    // Historical votes may not have explicit restrictedTimeSlots, treat as all allowed.
    if (!restrictedTimeSlots?.length) {
      return new Set(daysOfWeek.flatMap((_, dayIndex) => hours.map((hour) => toSlotKey(dayIndex, hour))));
    }
    return new Set(restrictedTimeSlots);
  }, [restrictedTimeSlots]);

  const countMap = useMemo(() => {
    const map = new Map();
    aggregatedResults.forEach(({ timeSlot, count }) => {
      map.set(timeSlot, count);
    });
    return map;
  }, [aggregatedResults]);

  const visibleHours = hours;

  const filteredSlots = useMemo(() => {
    const daySet = new Set(selectedDays);
    const slots = [];

    daysOfWeek.forEach((_, dayIndex) => {
      if (!daySet.has(dayIndex)) return;
      visibleHours.forEach((hour) => {
        const timeSlot = toSlotKey(dayIndex, hour);
        const count = countMap.get(timeSlot) || 0;
        const isAllowed = allowedSlotSet.has(timeSlot);
        slots.push({ timeSlot, dayIndex, hour, count, isAllowed });
      });
    });

    return slots;
  }, [selectedDays, visibleHours, countMap, allowedSlotSet]);

  const rankedSlots = useMemo(() => {
    return filteredSlots
      .filter((slot) => slot.isAllowed)
      .filter((slot) => slot.count > 0)
      .sort((a, b) => b.count - a.count || a.dayIndex - b.dayIndex || a.hour - b.hour);
  }, [filteredSlots]);

  const topThree = rankedSlots.slice(0, 3);
  const maxVotes = rankedSlots[0]?.count || 0;
  const totalVoters = votes.length;

  const bestDay = useMemo(() => {
    if (!filteredSlots.length) return null;

    const dayTotals = new Map();
    filteredSlots.forEach(({ dayIndex, count, isAllowed }) => {
      if (!isAllowed) return;
      dayTotals.set(dayIndex, (dayTotals.get(dayIndex) || 0) + count);
    });

    const sorted = [...dayTotals.entries()].sort((a, b) => b[1] - a[1]);
    if (!sorted.length) return null;

    return {
      dayIndex: sorted[0][0],
      count: sorted[0][1]
    };
  }, [filteredSlots]);

  const bestTwoHourWindow = useMemo(() => {
    const selectedDaySet = new Set(selectedDays);
    let best = null;

    daysOfWeek.forEach((_, dayIndex) => {
      if (!selectedDaySet.has(dayIndex)) return;

      const hoursToUse = visibleHours;
      for (let i = 0; i < hoursToUse.length - 1; i += 1) {
        const h1 = hoursToUse[i];
        const h2 = hoursToUse[i + 1];
        if (h2 !== h1 + 1) continue;

        const s1 = toSlotKey(dayIndex, h1);
        const s2 = toSlotKey(dayIndex, h2);
        if (!allowedSlotSet.has(s1) || !allowedSlotSet.has(s2)) continue;

        const total = (countMap.get(s1) || 0) + (countMap.get(s2) || 0);
        if (!best || total > best.count) {
          best = {
            dayIndex,
            startHour: h1,
            endHour: h2,
            count: total
          };
        }
      }
    });

    return best;
  }, [selectedDays, visibleHours, allowedSlotSet, countMap]);

  const topBarData = useMemo(() => {
    return rankedSlots.slice(0, 10).map((slot) => ({
      label: formatSlotLabel(slot.timeSlot),
      votes: slot.count
    }));
  }, [rankedSlots]);

  const detailSlots = useMemo(() => {
    return selectedDays.flatMap((dayIndex) =>
      visibleHours.map((hour) => ({
        dayIndex,
        hour,
        timeSlot: toSlotKey(dayIndex, hour)
      }))
    );
  }, [selectedDays, visibleHours]);

  const suggestedTimes = useMemo(() => {
    const suggestions = [];

    if (topThree[0]) {
      suggestions.push({
        title: 'Best single slot',
        value: `${formatSlotLabel(topThree[0].timeSlot)} (${topThree[0].count} votes)`
      });
    }

    if (bestTwoHourWindow && bestTwoHourWindow.count > 0) {
      suggestions.push({
        title: 'Best 2-hour window',
        value: `${daysOfWeek[bestTwoHourWindow.dayIndex]} ${formatHour(bestTwoHourWindow.startHour)}-${formatHour(bestTwoHourWindow.endHour + 1)} (${bestTwoHourWindow.count} total votes)`
      });
    }

    if (topThree[1]) {
      suggestions.push({
        title: 'Backup option',
        value: `${formatSlotLabel(topThree[1].timeSlot)} (${topThree[1].count} votes)`
      });
    }

    return suggestions;
  }, [topThree, bestTwoHourWindow]);

  const toggleDay = (dayIndex) => {
    setSelectedDays((prev) => {
      if (prev.includes(dayIndex)) {
        const next = prev.filter((d) => d !== dayIndex);
        return next.length ? next : prev;
      }
      return [...prev, dayIndex].sort((a, b) => a - b);
    });
  };

  if (!vote) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Vote Results"
      className="results-modal"
      overlayClassName="results-modal-overlay"
    >
      <h2>Vote Results for {name}</h2>

      {type === 'time_selection' && (
        <>
          <div className="rm-summary-grid">
            <div className="rm-summary-card">
              <span className="rm-summary-label">Total Ballots</span>
              <strong>{totalVoters}</strong>
            </div>
            <div className="rm-summary-card">
              <span className="rm-summary-label">Top Slot Votes</span>
              <strong>{maxVotes}</strong>
            </div>
            <div className="rm-summary-card">
              <span className="rm-summary-label">Best Day</span>
              <strong>{bestDay ? `${daysOfWeek[bestDay.dayIndex]} (${bestDay.count})` : '—'}</strong>
            </div>
            <div className="rm-summary-card">
              <span className="rm-summary-label">Best 2-Hour Window</span>
              <strong>
                {bestTwoHourWindow
                  ? `${daysOfWeek[bestTwoHourWindow.dayIndex]} ${formatHour(bestTwoHourWindow.startHour)}-${formatHour(bestTwoHourWindow.endHour + 1)}`
                  : '—'}
              </strong>
            </div>
          </div>

          <h3>Top 3 Results</h3>
          <div className="rm-top-three-grid">
            {topThree.map((slot, index) => {
              const percentage = totalVoters ? Math.round((slot.count / totalVoters) * 100) : 0;
              const behindTop = topThree[0] ? topThree[0].count - slot.count : 0;
              return (
                <div key={slot.timeSlot} className="rm-top-three-card">
                  <div className="rm-rank-badge">#{index + 1}</div>
                  <div className="rm-top-three-main">{formatSlotLabel(slot.timeSlot)}</div>
                  <div className="rm-top-three-meta">
                    <strong>{slot.count}</strong> votes • {percentage}%
                    {index > 0 && ` • ${behindTop} behind #1`}
                  </div>
                </div>
              );
            })}
            {!topThree.length && <div className="rm-top-three-empty">No votes have been cast yet.</div>}
          </div>

          {suggestedTimes.length > 0 && (
            <div className="rm-suggestions">
              <h3>Suggested Times</h3>
              <ul>
                {suggestedTimes.map((suggestion) => (
                  <li key={suggestion.title}>
                    <strong>{suggestion.title}:</strong> {suggestion.value}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rm-controls-wrap">
            <div className="rm-view-toggle" role="tablist" aria-label="Results view">
              <button className={activeView === 'heatmap' ? 'active' : ''} onClick={() => setActiveView('heatmap')}>
                Heatmap
              </button>
              <button className={activeView === 'bars' ? 'active' : ''} onClick={() => setActiveView('bars')}>
                Ranked Bars
              </button>
              <button className={activeView === 'details' ? 'active' : ''} onClick={() => setActiveView('details')}>
                Voter Detail
              </button>
            </div>

            <div className="rm-day-filters">
              {daysOfWeek.map((day, idx) => (
                <button
                  key={day}
                  className={selectedDays.includes(idx) ? 'active' : ''}
                  onClick={() => toggleDay(idx)}
                >
                  {day}
                </button>
              ))}
            </div>

            <div className="rm-checkboxes">
              <label>
                <input
                  type="checkbox"
                  checked={showUnavailableSlots}
                  onChange={(e) => setShowUnavailableSlots(e.target.checked)}
                />
                Show unavailable slots overlay
              </label>
            </div>
          </div>

          {activeView === 'heatmap' && (
            <div className="rm-heatmap-wrapper">
              <div className="rm-heatmap-legend">
                <span>Low</span>
                <div className="rm-legend-gradient" />
                <span>High</span>
              </div>

              <div className="rm-heatmap-scroll">
                <div
                  className="rm-heatmap-grid"
                  style={{
                    gridTemplateColumns: `84px repeat(${visibleHours.length}, minmax(26px, 1fr))`
                  }}
                >
                  <div className="rm-corner-cell" />
                  {visibleHours.map((hour) => (
                    <div key={`h-${hour}`} className="rm-hour-label">
                      {String(hour).padStart(2, '0')}
                    </div>
                  ))}

                  {daysOfWeek.map((day, dayIndex) => {
                    if (!selectedDays.includes(dayIndex)) return null;
                    return (
                      <React.Fragment key={`day-${day}`}>
                        <div className="rm-day-label">{day}</div>
                        {visibleHours.map((hour) => {
                          const timeSlot = toSlotKey(dayIndex, hour);
                          const count = countMap.get(timeSlot) || 0;
                          const isAllowed = allowedSlotSet.has(timeSlot);
                          const showAsUnavailable = !isAllowed && showUnavailableSlots;

                          return (
                            <div
                              key={timeSlot}
                              className={`rm-heat-cell ${showAsUnavailable ? 'unavailable' : ''}`}
                              style={{
                                backgroundColor: getHeatColor(count, maxVotes, isAllowed),
                                color: maxVotes && count / maxVotes > 0.55 ? '#fff' : '#0f172a'
                              }}
                              title={`${day} ${formatHour(hour)} • ${count} votes${!isAllowed ? ' • unavailable' : ''}`}
                            >
                              {count > 0 ? count : ''}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              <p className="rm-axis-help">X-axis: Hour of day • Y-axis: Day of week</p>
            </div>
          )}

          {activeView === 'bars' && (
            <div className="rm-chart-wrap">
              {topBarData.length ? (
                <ResponsiveContainer width="100%" height={360}>
                  <BarChart data={topBarData} margin={{ top: 12, right: 24, left: 12, bottom: 76 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" angle={-28} textAnchor="end" interval={0} height={92} />
                    <YAxis allowDecimals={false} label={{ value: 'Votes', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="votes" fill="#2b4f72" name="Votes">
                      <LabelList dataKey="votes" position="top" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p>No data available for ranked bars.</p>
              )}
            </div>
          )}

          {activeView === 'details' && (
            <div className="rm-voter-detail-wrap">
              <div className="rm-voter-grid-scroll">
                <div
                  className="rm-voter-grid"
                  style={{ gridTemplateColumns: `160px repeat(${detailSlots.length}, minmax(18px, 1fr))` }}
                >
                  <div className="rm-voter-header sticky-left">Voter</div>
                  {detailSlots.map(({ dayIndex, hour, timeSlot }) => (
                    <div
                      key={`vh-${timeSlot}`}
                      className={`rm-voter-hour-header ${hour === visibleHours[0] ? 'day-start' : ''}`}
                      title={`${daysOfWeek[dayIndex]} ${formatHour(hour)}`}
                    >
                      {hour % 2 === 0 ? String(hour).padStart(2, '0') : ''}
                    </div>
                  ))}

                  <div className="rm-voter-day-row-label sticky-left">Day</div>
                  {detailSlots.map(({ dayIndex, hour, timeSlot }) => (
                    <div
                      key={`vd-${timeSlot}`}
                      className={`rm-voter-day-header ${hour === visibleHours[0] ? 'day-start' : ''}`}
                    >
                      {hour === visibleHours[0] ? daysOfWeek[dayIndex] : ''}
                    </div>
                  ))}

                  {votes.map(({ name: voterName, selection }, rowIndex) => {
                    const selectedSet = new Set(selection || []);
                    return (
                      <React.Fragment key={`${voterName}-${rowIndex}`}>
                        <div className="rm-voter-name sticky-left">{voterName}</div>
                        {detailSlots.map(({ dayIndex, hour, timeSlot }) => {
                          const chosen = selectedSet.has(timeSlot);
                          return (
                            <div
                              key={`${voterName}-${timeSlot}`}
                              className={`rm-voter-cell ${chosen ? 'selected' : ''} ${hour === visibleHours[0] ? 'day-start' : ''}`}
                              title={`${voterName} • ${daysOfWeek[dayIndex]} ${formatHour(hour)}${chosen ? ' selected' : ''}`}
                            />
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              <p className="rm-axis-help">
                Day labels are shown in-header, hour labels every 2 hours. Vertical separators indicate day boundaries.
              </p>
            </div>
          )}
        </>
      )}

      {type === 'custom_selection' && (
        <>
          <h3>Option Results</h3>
          <div className="bar-graph-container">
            {(() => {
              const maxCount = Math.max(...aggregatedResults.map((r) => r.count), 1);
              return aggregatedResults.map(({ option, count }, index) => (
                <div key={index} className="bar-graph-row">
                  <span className="option-label">{option}</span>
                  <div className="bar-container">
                    <div className="bar" style={{ width: `${(count / maxCount) * 100}%` }}>
                      {count}
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>
        </>
      )}

      <button className="rm-close-button" onClick={onRequestClose}>Close</button>
    </Modal>
  );
};

export default ResultsModal;
